import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { IonPage } from '@ionic/react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { AllieContent } from '@allie/app-ui-lib';
import { PermissionAction, PermissionSubject, isMobile } from '../../helpers';
import { findPermissionWithAction } from '../../helpers/permissionsChecking';
import { HeaderWrapper } from './components/HeaderWrapper';
import { AppContext } from '../../context';
import { useAllieAuth, useAllieRouting, useAllieStorage, useSupport } from '../../hooks';

import {
  ForbiddenPage,
  EfficiencyPage,
  WelcomePage,
  ConfigurationsPage,
  FactoryGptPage,
  ScrapPage,
  NotificationsPage,
} from '../../pages';
import './Page.scss';

dayjs.locale(navigator.language.split('-')[0]);

type Pages =
  | 'welcome'
  | 'efficiency'
  | 'configurations'
  | 'factory-gpt'
  | 'forbidden'
  | 'notifications';

const namespace = 'page';

export const Page: React.FC = () => {
  // loginOpen = undefined when is dismissed to avoid reopen infinitely
  const [loginOpen, setLoginOpen] = useState<boolean | undefined>(false);
  const { name } = useParams<{ name: Pages }>();
  const history = useHistory();
  const { saveLastVisitedPage, getDefaultPage, getLastVisitedPageByUser } = useAllieRouting();
  const { setDeeplink, getDeeplink } = useAllieStorage();
  const { lang } = useContext(AppContext);

  const { 
    login, 
    loggedUser, 
    logout, 
    permissions, 
    openLogin, 
    guestModeLoading, 
    guestModeFailed, 
    isChangingUser 
  } = useAllieAuth();
  useSupport();

  const accountPermissionsPage = [
    PermissionSubject.CONFIG_ACCOUNT_STATIONS,
    PermissionSubject.CONFIG_ACCOUNT_USERS,
    PermissionSubject.CONFIG_ACCOUNT_PRODUCTS,
    PermissionSubject.CONFIG_ACCOUNT_MACHINES,
    PermissionSubject.CONFIG_ACCOUNT_DOWNTIME_REASONS,
    PermissionSubject.CONFIG_ACCOUNT_QUALITY_REASONS,
    PermissionSubject.CONFIG_ACCOUNT_BULK_DATA,
    PermissionSubject.CONFIG_GATEWAYS,
    PermissionSubject.CONFIG_METRICS,
  ];

  type PagesMap = Record<Pages, { hasPermission: boolean | undefined; component: ReactNode }>;

  const pages: PagesMap = useMemo(
    () => ({
      forbidden: {
        hasPermission: true,
        component: <ForbiddenPage />,
      },
      welcome: {
        hasPermission: true,
        component: <WelcomePage />,
      },
      efficiency: {
        hasPermission:
          permissions?.eff_stations_dashboard?.view ||
          permissions?.eff_summary_dashboard?.view ||
          permissions?.eff_station_assign_stop?.create,
        component: <EfficiencyPage basePath="efficiency" openLogin={openLogin} />,
      },
      configurations: {
        hasPermission:
          permissions?.[
            findPermissionWithAction(accountPermissionsPage, PermissionAction.VIEW, permissions)
          ]?.view,
        component: <ConfigurationsPage basePath="configurations" openLogin={openLogin} />,
      },
      'factory-gpt': {
        hasPermission: permissions?.factory_gpt?.view,
        component: <FactoryGptPage basePath="factory-gpt" openLogin={openLogin} />,
      },
      scrap: {
        hasPermission: true,
        component: <ScrapPage basePath="scrap" openLogin={openLogin} />,
      },
      notifications: {
        hasPermission: true,
        component: <NotificationsPage basePath="notifications" openLogin={openLogin} />,
      },
    }),
    [permissions, openLogin, lang]
  );

  useEffect(() => {
    if (!name) history.push(getDefaultPage());
  }, [name, history, getDefaultPage]);

  const availablePages = useMemo(() => {
    const keys: Pages[] = Object.keys(pages).map((p) => p as Pages);

    return keys.reduce((acum: Partial<PagesMap>, k: Pages) => {
      if (pages[k].hasPermission) acum[k] = pages[k];
      return acum;
    }, {});
  }, [pages]);

  const forbiddenAccess = useMemo(
    () => (
      !loginOpen &&
      !guestModeLoading &&
      !guestModeFailed &&
      !isChangingUser &&
      name &&
      !availablePages[name] &&
      !!loggedUser?.id
    ), [
      loginOpen,
      name,
      availablePages,
      guestModeLoading,
      guestModeFailed,
      isChangingUser,
      loggedUser?.id,
    ]
  );

  useEffect(() => {
    if (
      Object.keys(loggedUser || {}).length === 0 &&
      !loginOpen &&
      !guestModeLoading &&
      !guestModeFailed &&
      !isChangingUser
    ) {
      logout(false);
    }
  }, [loggedUser]);

  // ACCESS/AUTH SCENARIOS
  useEffect(() => {
    if (
      forbiddenAccess &&
      loginOpen !== undefined && // Not dismissed login popup
      Object.keys(pages).some((p) => window.location.pathname.includes(p)) // Valid path
    ) {
      setLoginOpen(true);
      setDeeplink(`${window.location.pathname}${window.location.search}`);

      if (Object.keys(loggedUser || {}).length) {
        // EXCEED PERMISSIONS --> Open login popup
        login(false, () => {
          // If popup is dismissed avoid reopen login popup & clean deeplink
          setLoginOpen(undefined);
          getDeeplink();
        });
      } else {
        // UNAUTHENTICATED --> Try to do guest login, if fails is redirect to /login
        logout(false);
      }
    }
  }, [forbiddenAccess]);

  useEffect(() => {
    if (loggedUser?.id === 0 && name === 'forbidden') {
      // const lastVisitedPage = getLastVisitedPageByUser(loggedUser.id);
      history.push(getDefaultPage());
    }
  }, [loggedUser, name]);

  // SAVE LAST PAGE
  useEffect(() => {
    if (saveLastVisitedPage && !forbiddenAccess && availablePages[name]) {
      saveLastVisitedPage(`${window.location.pathname}${window.location.search}`);
    }
  }, [window.location.href]);

  return (
    <IonPage className={namespace}>
      {isMobile && <HeaderWrapper />}
      <AllieContent style={{ paddingTop: 0 }} id="main-app-content">
        {!isMobile && <HeaderWrapper />}
        {forbiddenAccess && <ForbiddenPage />}
        <div style={{ paddingTop: isMobile ? 75 : 0 }}>
          {name && availablePages[name] && availablePages[name]?.component}
        </div>
      </AllieContent>
    </IonPage>
  );
};
